/**
 * WordPress dependencies
 */
import { __ } from '@wordpress/i18n';
import { Fragment } from '@wordpress/element';
import {
	TabPanel,
	__experimentalDivider as Divider,
	__experimentalUnitControl as UnitControl,
	__experimentalNumberControl as NumberControl,
	ToggleControl,
	SelectControl,
} from '@wordpress/components';
import { addFilter } from '@wordpress/hooks';

/**
 * External dependencies
 */
import { forOwn } from 'lodash';

/**
 * Inner dependencies.
 */
import {
	ColorControl,
	GradientColorControl,
} from '../../../extensions/controls/color-options';
import PaddingControl from '../../../extensions/controls/padding-control';
import Accordion from '../../../components/Accordion';
import blockInfo from '../constants';
const { hookPrefix } = blockInfo;

addFilter(
	`gutenify-block-${ hookPrefix }-options-tab-style`,
	`Gutenify/Block/${ hookPrefix }/InspectorControls/BasicControls/tabs/style`,
	( content, props ) => {
		const { attributes, setAttributes } = props;
		const {
			source,
			size,
			iconImage,
			imageSize,
			customSize,
			blockAdvanceOptions,
			borderRadius,
			borderWidth,
		} = attributes;

		const getImageSize = ( sizes ) => {
			const sizeArr = [];
			forOwn( sizes, function( value, key ) {
				const p = { value: key, label: key };
				sizeArr.push( p );
			} );
			return sizeArr;
		};
		let imageSizeOptions = [
			{ value: 'thumbnail', label: __( 'Thumbnail' ) },
			{ value: 'medium', label: __( 'Medium' ) },
			{ value: 'full', label: __( 'Large' ) },
		];
		if ( iconImage && iconImage.sizes ) {
			imageSizeOptions = getImageSize( iconImage.sizes );
		}
		return [
			...content,
			<Fragment key={ `gutenify-block-${ hookPrefix }-options-tab-content-basic` }>
				<Accordion tabs={ [
					{
						name: 'icon',
						initialOpen: true,
						label: () => {
							return __( 'Icon style' );
						},
						cb: () => {
							return <>
								<TabPanel
									className="gutenify-editor-tab-panel gutenify-editor-tab-ovel"
									activeClass="active-tab"
									tabs={ [
										{
											name: 'normal',
											title: __( 'Normal' ),
											className: 'gutenify-color-normal-tab',
										},
										{
											name: 'hover',
											title: __( 'Hover' ),
											className: 'gutenify-color-hover-tab',
										},
									] }
								>
									{ ( tabInner ) => {
										return (
											<>
												{ 'normal' === tabInner.name && (
													<>
														{ 'icon' === source && (
															<>
																<ColorControl
																	label={ __( 'Color' ) }
																	onChange={ ( color ) => {
																		const data = {
																			blockAdvanceOptions: {
																				...blockAdvanceOptions,
																				textColor: color,
																			},
																		};
																		setAttributes( data );
																	} }
																	value={ blockAdvanceOptions.textColor }
																/>
															</>
														) }
														<GradientColorControl
															label={ __( 'Background Color' ) }
															colorValue={ blockAdvanceOptions.backgroundColor }
															gradientValue={ blockAdvanceOptions.backgroundGradient }
															onChange={ ( val ) => {
																const data = {
																	blockAdvanceOptions: {
																		...blockAdvanceOptions,
																		...val,
																	},
																};
																setAttributes( data );
															} }
														/>
														<ColorControl
															label={ __( 'Border Color' ) }
															onChange={ ( color ) => {
																const data = {
																	blockAdvanceOptions: {
																		...blockAdvanceOptions,
																		borderColor: color,
																	},
																};
																setAttributes( data );
															} }
															value={ blockAdvanceOptions.borderColor }
														/>
													</>
												) }
												{ 'hover' === tabInner.name && (
													<>
														{ 'icon' === source && (
															<>
																<ColorControl
																	label={ __( 'Color' ) }
																	onChange={ ( color ) => {
																		const data = {
																			blockAdvanceOptions: {
																				...blockAdvanceOptions,
																				hoverTextColor: color,
																			},
																		};
																		setAttributes( data );
																	} }
																	value={ blockAdvanceOptions.hoverTextColor }
																/>
															</>
														) }
														<GradientColorControl
															label={ __( 'Background Color' ) }
															colorValue={ blockAdvanceOptions.hoverBackgroundColor }
															gradientValue={
																blockAdvanceOptions.hoverBackgroundGradient
															}
															onChange={ ( val ) => {
																const data = {
																	blockAdvanceOptions: {
																		...blockAdvanceOptions,
																		...{
																			hoverBackgroundColor: val.backgroundColor,
																			hoverBackgroundGradient: val.backgroundGradient,
																		},
																	},
																};
																setAttributes( data );
															} }
														/>
														<ColorControl
															label={ __( 'Border Color' ) }
															onChange={ ( color ) => {
																const data = {
																	blockAdvanceOptions: {
																		...blockAdvanceOptions,
																		hoverBorderColor: color,
																	},
																};
																setAttributes( data );
															} }
															value={ blockAdvanceOptions.hoverBorderColor }
														/>
													</>
												) }
												<Divider />
											</>
										);
									} }
								</TabPanel>

								{ 'icon' === source && (
									<>
										<NumberControl
											label={ __( 'Size' ) }
											// help="Additional info about this."
											// beforeIcon={ arrowDown }
											// afterIcon={ arrowUp }
											// allowReset
											// resetFallbackValue={ 10 }
											// step={ 1 }
											// icon={ more }
											// separatorType="none"
											// isShiftStepEnabled
											value={ size }
											onChange={ ( val ) => {
												const data = { size: val };
												setAttributes( data );
											} }
											min={ 10 }
											// max={ 300 }
											// withInputField={ true }
										/>
									</>
								) }
								{ 'image' === source && iconImage && iconImage.url !== 'null' && iconImage.url !== '' && ( <Fragment>
									<SelectControl
										label={ __( 'Image Size' ) }
										options={ imageSizeOptions }
										value={ imageSize }
										onChange={ ( value ) => {
											const data = { imageSize: value };
											setAttributes( data );
										} }
									/>

									<ToggleControl
										label={ __( 'Custom Width' ) }
										checked={ customSize }
										onChange={ () => {
											const data = { customSize: ! customSize };
											setAttributes( data );
										} }
										help={ __(
											'Turn this off to inherit the natural width of Image.'
										) }
									/>
									{ customSize && (
										<NumberControl
											label={ __( 'Width (px)' ) }
											value={ size }
											onChange={ ( value ) => {
												const data = { size: value };
												setAttributes( data );
											} }
											min={ 0 }
											max={ 500 }
											// beforeIcon=""
											// allowReset
										/>
									) }
								</Fragment>
								) }

								<PaddingControl
									{ ...props }
									parentKey="blockAdvanceOptions"
									label={ __( 'Padding' ) }
									property="iconPadding"
									onChange={ ( data ) => {
										// updateBlockStyle( data, props );
									} }
								/>

								<NumberControl
									label={ __( 'Border Width' ) }
									// help="Additional info about this."
									// beforeIcon={ arrowDown }
									// afterIcon={ arrowUp }
									// allowReset
									// resetFallbackValue={ 0 }
									// step={ 1 }
									// icon={ more }
									// separatorType="none"
									// isShiftStepEnabled
									value={ borderWidth }
									onChange={ ( val ) => {
										const data = { borderWidth: val };
										setAttributes( data );
									} }
									min={ 0 }
									max={ 300 }
									// withInputField={ true }
								/>

								<UnitControl
									label={ __( 'Border Radius' ) }
									value={ borderRadius }
									onChange={ ( val ) => {
										const data = { borderRadius: val };
										setAttributes( data );
									} }
									min={ 0 }
									max={ 300 }
									// withInputField={ true }
								/>
							</>;
						},
					},
				] } />
			</Fragment>,
		];
	}
);

